import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import wemeetClient from '@utils/wemeetClient';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getMicStatus, setLocalAudioStream, getLocalAudioStream } from '@store/media/media.slice';

import { openToast } from '@component/toast';

/**
 * 用户静音的时候检测有无说话，进行提示
 *
 * 实现方案
 * 1. 额外创建一个本地音视频流
 * 2.
 */
export default function VoiceActivityDetection() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const micOn = useAppSelector(getMicStatus);

  /** ******** 以下为静音时用户说话提示开麦相关变量及逻辑 ************ */
  const localAudioStream = useAppSelector(getLocalAudioStream);
  const [localAudioRAF, setLocalAudioRAF] = useState(-1);
  const [hasRAF, setHasRAF] = useState(false);

  if (!localAudioStream) {
    // 创建 & 初始化本地音频流，与入会使用不同的userId，避免互相干扰；
    const stream = wemeetClient.trtcClient.createLocalStream({
      userId: 'local-stream',
      audioState: true,
      videoState: false,
    });
    stream
      .initialize()
      .then(() => {
        console.log('studio initialize localAudioStream success');
        dispatch(setLocalAudioStream(stream));
      })
      .catch((error: object) => {
        console.error(`studio failed localAudioStream localStream ${error}`);
      });
  }

  let localAudioStart = -1; //  第一次tdio检测到本地有声音
  let localAudioOffStart = -1; //  第一次检测到本地无声音
  let lastLocalAudioTime = -1; //  最后一次检测到本地有声音的时间；
  let lastLocalAudioOffTime = -1; //  最后一次检测到本地无声音的时间；
  let localAudioTipShowed = false; // 是否已提示过用户开麦

  useEffect(
    () => () => {
      localAudioStream.close();
      clearLocalAudioRAF();
    },
    [],
  );

  /** 开麦提示相关逻辑 */
  useEffect(() => {
    // console.log(
    //   'studio micOn',
    //   micOn,
    //   'localAudioStream',
    //   localAudioStream,
    //   'localAudioRAF',
    //   localAudioRAF,
    //   'hasRAF',
    //   hasRAF,
    //   'localAudioTipShowed',
    //   localAudioTipShowed,
    // );
    if (!micOn) {
      if (localAudioStream && !hasRAF) startListenLocalAudioStream();
    } else {
      localAudioStream.stop();
      clearLocalAudioRAF();
      localAudioTipShowed = false;
    }
  }, [micOn, localAudioStream, hasRAF]);

  /** @desc 开始检测本地音量 */
  const startListenLocalAudioStream = () => {
    console.log('studio startListenLocalAudioStream');
    lastLocalAudioOffTime = performance.now();
    localAudioStream
      .play('local-stream', { muted: true })
      .then(() => {
        console.log('studio setLocalAudioRAF');
        startLocalAudioRAF();
      })
      .catch((error: object) => {
        console.log(`studio play localAudioStream error ${error}`);
      });
  };

  /** @desc 用RAF来轮询检测本地音量 */
  const startLocalAudioRAF = () => {
    const listenLocalAudio = (now: number) => {
      const volume = localAudioStream.getAudioLevel();
      // console.log('studio volume', volume);

      // 提示条件：音量大于0.1&&保持1.5秒以上，且关麦后只提示一次
      if (volume > 0.1) {
        if (lastLocalAudioTime - lastLocalAudioOffTime < 0) {
          // 此前一直处于无声音的状态
          localAudioStart = now;
        }
        // console.log('studio 持续有声音(ms)：', now - localAudioStart);
        if (!localAudioTipShowed && now - localAudioStart > 1500) {
          openToast(t('您已静音，请解除静音后发言'));
          localAudioTipShowed = true;
        }
        lastLocalAudioTime = now;
      } else {
        if (lastLocalAudioTime - lastLocalAudioOffTime > 0) {
          // 此前一直处于有声音的状态
          localAudioOffStart = now;
        }

        if (now - localAudioOffStart > 1000) {
          // 无声音超过1s，才认为真的无声音了
          // console.log('studio 持续无声音(ms)：', now - localAudioOffStart);
          lastLocalAudioOffTime = now;
        } else {
          // 1s内的认为是说话过程中的正常停顿，等价于有声音
          // console.log('studio 持续有声音-停顿(ms)：', now - localAudioStart);
          lastLocalAudioTime = now;
          lastLocalAudioOffTime = now;
        }
      }

      if (localAudioRAF < 0) {
        setHasRAF(true);
      }
      setLocalAudioRAF(requestAnimationFrame(listenLocalAudio));
    };

    listenLocalAudio(performance.now());
  };

  /** @desc 清除用来检测本地音量的RAF */
  const clearLocalAudioRAF = () => {
    cancelAnimationFrame(localAudioRAF);
    setLocalAudioRAF(-1);
    setHasRAF(false);
  };
  /** ******** 以上为静音时用户说话提示开麦相关逻辑 ************ */

  return <div style={{ display: 'none' }} id="local-stream" />;
}
